import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, ClipboardCheck, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  fmtMoney, monthLabel, periodFor, shiftPeriod, useSetEnrollment, useTankRentalCustomer, useTankRentalHistory, useUpdateSettings,
  type CustomerHistory, type HistoryAudit, type HistoryLedgerRow,
} from '@/hooks/useTankRentals';
import { auditDeltas, auditStatusBadge } from './RentalsAudits';
import { runningBalances } from '@/lib/cylinderLedger';

/**
 * One customer on the tank rental program: the switch, their rates (an
 * override per cylinder type — blank means the standard rate), where the
 * invoice email goes, notes, and a live preview of next month's invoice built
 * by the same rules the run uses.
 */
export default function RentalsCustomer() {
  const { qboId = '' } = useParams();
  const period = shiftPeriod(periodFor(new Date()), 1);
  const { data, isLoading, error } = useTankRentalCustomer(qboId, period);
  const [historyMonths, setHistoryMonths] = useState(24);
  const history = useTankRentalHistory(qboId, historyMonths);
  const setEnrollment = useSetEnrollment();
  const update = useUpdateSettings();

  const [rates, setRates] = useState<Record<string, string>>({});
  const [emails, setEmails] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const e = data.enrollment;
    const r: Record<string, string> = {};
    for (const [k, v] of Object.entries(e?.rate_overrides ?? {})) r[k] = String(v);
    setRates(r);
    setEmails((e?.email_to ?? []).join(', '));
    setEmailEnabled(e?.email_enabled ?? true);
    setNotes(e?.notes ?? '');
  }, [data]);

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-24 text-brix-muted"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Loading…</span></div>;
  if (error || !data) return <div className="p-4 text-sm text-brix-warning">Couldn’t load this customer: {error?.message}</div>;

  const enrolled = data.enrollment?.enrolled === true;
  const name = data.roster?.customer_name ?? `QuickBooks #${qboId}`;

  const save = async () => {
    setErr(null); setMsg(null);
    try {
      const rate_overrides: Record<string, number> = {};
      for (const [k, v] of Object.entries(rates)) {
        const t = v.trim();
        if (t === '') continue;
        const n = Number(t);
        if (!Number.isFinite(n) || n < 0) throw new Error(`Rate for ${k} must be a number`);
        rate_overrides[k] = n;
      }
      await update.mutateAsync({
        qbo_customer_id: qboId,
        rate_overrides,
        email_to: emails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean),
        email_enabled: emailEnabled,
        notes: notes.trim() || null,
      });
      setMsg('Saved.');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const toggle = async () => {
    setErr(null); setMsg(null);
    try {
      await setEnrollment.mutateAsync({ qbo_customer_id: qboId, enrolled: !enrolled });
      setMsg(enrolled ? 'Taken off the program. No rental will be billed from next month.' : 'On the program. Billed from next month’s run.');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div>
      <Link to="/audit/rentals" className="inline-flex items-center gap-1 text-xs text-brix-muted hover:text-brix-ink"><ArrowLeft className="h-3.5 w-3.5" /> All customers</Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">{name}</h1>
          <p className="text-xs text-brix-muted">
            QuickBooks #{qboId}
            {data.roster?.qbo_active === false && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-red-800">inactive in QuickBooks</span>}
            {data.roster?.portal_customer_id && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5">portal customer</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* A portal customer is audited through the driver's normal route; an
              account that exists only in QuickBooks through the qbo route. Either
              way the form comes back here when it is done. */}
          <Button asChild variant="secondary">
            <Link to={data.roster?.portal_customer_id ? `/audit/${data.roster.portal_customer_id}?from=rentals` : `/audit/qbo/${qboId}?from=rentals`}>
              <ClipboardCheck className="h-4 w-4" /> Start an audit
            </Link>
          </Button>
          <Button variant={enrolled ? 'secondary' : 'primary'} onClick={() => void toggle()} disabled={setEnrollment.isPending}>
            {enrolled ? 'Take off the program' : 'Put on the program'}
          </Button>
        </div>
      </div>

      {msg && <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{msg}</p>}
      {err && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
          <h2 className="font-display text-sm font-bold text-brix-ink">Rates</h2>
          <p className="mt-1 text-xs text-brix-muted">Leave blank for the standard rate (the last rate on their invoice, else the item price). A number here is per cylinder per month.</p>
          <div className="mt-3 space-y-2">
            {data.gases.map((g) => (
              <div key={g.label} className="flex items-center gap-3">
                <Label className="w-32 text-xs">{g.displayName}</Label>
                <Input
                  inputMode="decimal"
                  placeholder={`standard ${fmtMoney(g.defaultRate)}`}
                  value={rates[g.label] ?? ''}
                  onChange={(e) => setRates((s) => ({ ...s, [g.label]: e.target.value }))}
                  className="h-9 w-40"
                />
              </div>
            ))}
          </div>
          {Object.keys(data.enrollment?.negative_floors ?? {}).length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-amber-900">Billed at a floor while the count is negative</h3>
              <p className="mt-1 text-xs text-amber-900">
                The books say fewer than zero for the cylinders below. Until a driver counts the site, the monthly invoice bills the number shown and prints it as the new balance. The site stays on the audit list; filing an audit removes this.
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-950">
                {Object.entries(data.enrollment?.negative_floors ?? {}).map(([label, n]) => (
                  <li key={label} className="flex items-center justify-between gap-3">
                    <span>{n} × {label}</span>
                    <button
                      type="button"
                      className="text-xs underline"
                      disabled={update.isPending}
                      onClick={() => {
                        if (!window.confirm(`Remove the ${n}-tank floor on ${label}? A negative count will hold the invoice again.`)) return;
                        const rest = { ...(data.enrollment?.negative_floors ?? {}) };
                        delete rest[label];
                        void update.mutateAsync({ qbo_customer_id: qboId, negative_floors: rest }).then(() => setMsg('Floor removed.')).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
                      }}
                    >
                      remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <h2 className="mt-5 font-display text-sm font-bold text-brix-ink">Invoice email</h2>
          <p className="mt-1 text-xs text-brix-muted">Comma-separated. Blank uses the portal’s invoice routing for a portal customer, else the QuickBooks email{data.roster?.qbo_email ? ` (${data.roster.qbo_email})` : ''}.</p>
          <Input className="mt-2" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ap@customer.com, owner@customer.com" />
          <label className="mt-2 flex items-center gap-2 text-sm text-brix-ink">
            <input type="checkbox" checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)} />
            Email the invoice each month
          </label>
          <h2 className="mt-5 font-display text-sm font-bold text-brix-ink">Notes</h2>
          <textarea className="mt-2 w-full rounded-md border border-brix-border bg-brix-surface p-2 text-sm" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the office should know about this rental." />
          <div className="mt-3 flex justify-end"><Button onClick={() => void save()} disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save settings'}</Button></div>
        </section>

        <section className="rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
          <h2 className="font-display text-sm font-bold text-brix-ink">Next invoice — {monthLabel(period)}</h2>
          <p className="mt-1 text-xs text-brix-muted">{data.doc_number} · built from today’s counts; the run on the 1st uses the same rules.</p>
          {!enrolled && <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">Not on the program — this is what they <i>would</i> be billed.</p>}
          {data.plan.status !== 'draft' && <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"><b>{data.plan.status}</b> — {data.plan.reason}</p>}
          {data.plan.warnings.map((w) => <p key={w} className="mt-2 text-xs text-amber-800">⚠ {w}</p>)}
          <table className="mt-3 w-full text-xs">
            <thead className="text-left text-[10px] uppercase text-brix-muted"><tr><th className="py-1">Cylinder</th><th className="text-right">Prev</th><th className="text-right">+In</th><th className="text-right">−Out</th><th className="text-right">Now</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.plan.lines.map((l) => (
                <tr key={l.label} className="border-t border-brix-border"><td className="py-1">{l.display_name}</td><td className="text-right">{l.prev}</td><td className="text-right">{l.deliveries}</td><td className="text-right">{l.pickups}</td><td className="text-right font-medium">{l.qty}</td><td className="text-right">{fmtMoney(l.unit_price)}</td><td className="text-right">{fmtMoney(l.amount)}</td></tr>
              ))}
              {data.rows.filter((r) => !data.plan.lines.some((l) => l.label === r.label)).map((r) => (
                <tr key={r.label} className="border-t border-brix-border text-brix-muted"><td className="py-1">{r.label}</td><td className="text-right">{r.prev_balance}</td><td className="text-right">{r.deliveries}</td><td className="text-right">{r.pickups}</td><td className={`text-right ${r.new_balance < 0 ? 'text-red-700' : ''}`}>{r.new_balance}</td><td className="text-right">—</td><td className="text-right">not billed</td></tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t border-brix-border font-semibold"><td colSpan={6} className="py-1 text-right">Total</td><td className="text-right">{fmtMoney(data.plan.total)}</td></tr></tfoot>
          </table>
          {data.rows[0]?.anchor_doc && <p className="mt-2 text-[11px] text-brix-muted">Counts anchor on {data.rows[0].anchor_doc} ({data.rows[0].anchor_date}).</p>}
        </section>
      </div>

      <AuditsCard history={history.data} loading={history.isLoading} error={history.error} qboId={qboId} portalId={data.roster?.portal_customer_id ?? null} />
      <HistoryCard history={history.data} loading={history.isLoading} error={history.error} months={historyMonths} onMonths={setHistoryMonths} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audits & adjustments — every on-site count taken here, with its photos
// ---------------------------------------------------------------------------

function AuditsCard({ history, loading, error, qboId, portalId }: { history: CustomerHistory | undefined; loading: boolean; error: Error | null; qboId: string; portalId: string | null }) {
  const audits = history?.audits ?? [];
  const openAlerts = (history?.alerts ?? []).filter((a) => !a.resolved_at);
  return (
    <section className="mt-4 rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-sm font-bold text-brix-ink">Audits & adjustments</h2>
          <p className="mt-1 text-xs text-brix-muted">Every on-site count a driver filed here. A count that differed from the books became a $0 CR-ADJ invoice, which is what moved the number below.</p>
        </div>
        <Link to={portalId ? `/audit/${portalId}?from=rentals` : `/audit/qbo/${qboId}?from=rentals`} className="inline-flex items-center gap-1 text-xs font-medium text-brix-primary hover:underline">
          <ClipboardCheck className="h-3.5 w-3.5" /> Start an audit
        </Link>
      </div>

      {openAlerts.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <b>Needs a count.</b> The books and the yard already disagree here:
          {openAlerts.map((a) => (
            <div key={a.id} className="mt-1">
              {a.label} — {a.cause ?? 'count went negative'}{a.first_doc_number ? ` on ${a.first_doc_number}` : ''}{a.first_date ? ` (${a.first_date})` : ''}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-brix-muted"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-xs">Loading…</span></div>
      ) : error ? (
        <p className="mt-3 text-xs text-brix-warning">Couldn’t load the audits: {error.message}</p>
      ) : audits.length === 0 ? (
        <p className="mt-3 text-xs text-brix-muted">No audit has been taken at this account.</p>
      ) : (
        <div className="mt-3 divide-y divide-brix-border">
          {audits.map((a) => <AuditRow key={a.id} a={a} />)}
        </div>
      )}
    </section>
  );
}

function AuditRow({ a }: { a: HistoryAudit }) {
  const counts = a.final_counts ?? a.counts;
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-semibold text-brix-ink">{a.doc_number}</span>
          {auditStatusBadge(a.status)}
          <span className="text-xs text-brix-muted">{new Date(a.created_at).toLocaleString()}</span>
        </div>
        <div className="mt-1 text-xs text-brix-ink">{auditDeltas({ counts })}</div>
        <div className="mt-1 text-xs text-brix-muted">
          Counted by {a.signed_name}
          {a.driver_name && a.driver_name !== a.signed_name ? ` · driver ${a.driver_name}` : ''}
          {a.manager_name ? ` · manager ${a.manager_name} signed` : ''}
          {a.approved_by_email ? ` · ${a.status === 'declined' ? 'declined' : 'approved'} by ${a.approved_by_email}` : ''}
          {a.final_counts ? ' · counts amended at approval' : ''}
        </div>
        {a.notes ? <div className="mt-1 text-xs italic text-brix-muted">“{a.notes}”</div> : null}
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          <Link to={`/admin/audits/${a.id}`} className="inline-flex items-center gap-1 text-brix-primary hover:underline">
            <Camera className="h-3.5 w-3.5" /> Open the audit record{a.photos.length ? ` · ${a.photos.length} photo${a.photos.length === 1 ? '' : 's'}` : ''}
          </Link>
          {a.qbo_invoice_id ? (
            <a href={`https://app.qbo.intuit.com/app/invoice?txnId=${a.qbo_invoice_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brix-muted hover:underline">
              <ExternalLink className="h-3 w-3" /> {a.doc_number} in QuickBooks
            </a>
          ) : null}
        </div>
      </div>
      {a.photos.length > 0 && (
        <div className="flex gap-1">
          {a.photos.slice(0, 4).map((p) => (
            <a key={p.path} href={p.url} target="_blank" rel="noreferrer" className="block h-14 w-14 overflow-hidden rounded border border-brix-border bg-brix-surface-alt">
              <img src={p.url} alt="Audit photo" className="h-full w-full object-cover" />
            </a>
          ))}
          {a.photos.length > 4 && <Link to={`/admin/audits/${a.id}`} className="flex h-14 w-14 items-center justify-center rounded border border-brix-border text-xs text-brix-muted">+{a.photos.length - 4}</Link>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History — the same ledger the customer sees on /cylinders, for the office
// ---------------------------------------------------------------------------

const KIND_LABEL: Record<HistoryLedgerRow['kind'], string> = {
  rental: 'Rental invoice',
  delivery: 'Delivered',
  pickup: 'Picked up',
  adjustment: 'Audit adjustment',
  system_adjustment: 'System adjustment',
};

function HistoryCard({ history, loading, error, months, onMonths }: { history: CustomerHistory | undefined; loading: boolean; error: Error | null; months: number; onMonths: (m: number) => void }) {
  // Balance as of each row's day, walked oldest-first, then shown newest first —
  // the question is "how did it get to today's number".
  const ledger = history?.ledger ?? [];
  const balances = runningBalances(ledger);
  const rows = ledger.map((r, i) => ({ ...r, bal: balances[i]! })).reverse();
  const unanchored = rows.some((r) => !r.bal.anchored);
  const auditByInvoice = new Map((history?.audits ?? []).filter((a) => a.qbo_invoice_id).map((a) => [a.qbo_invoice_id!, a.id] as const));
  return (
    <section className="mt-4 rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-sm font-bold text-brix-ink">Cylinder history</h2>
          <p className="mt-1 text-xs text-brix-muted">How the count got here — the same ledger the customer sees on their Cylinders page. Each rental invoice restates the balance; deliveries, pickups and audit adjustments move it.</p>
        </div>
        <div className="flex gap-1 text-xs">
          {[12, 24, 60].map((m) => (
            <button key={m} type="button" onClick={() => onMonths(m)} className={`rounded-full border px-2.5 py-0.5 ${months === m ? 'border-brix-primary bg-brix-primary text-white' : 'border-brix-border text-brix-ink'}`}>{m === 60 ? '5 years' : `${m} months`}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-brix-muted"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-xs">Loading…</span></div>
      ) : error ? (
        <p className="mt-3 text-xs text-brix-warning">Couldn’t load the history: {error.message}</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-brix-muted">No cylinder activity in the last {months} months.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-brix-muted">
              <tr><th className="py-1 pr-3">Date</th><th className="py-1 pr-3">What</th><th className="py-1 pr-3">Cylinder</th><th className="py-1 pr-3 text-right">Qty</th><th className="py-1 pr-3 text-right">Balance</th><th className="py-1 pr-3">Document</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const signed = r.bal.signed;
                const auditId = r.qbo_invoice_id ? auditByInvoice.get(r.qbo_invoice_id) : undefined;
                return (
                  <tr key={`${r.doc_number}-${r.label}-${i}`} className={`border-t border-brix-border ${r.kind === 'rental' ? 'bg-brix-surface-alt/60' : ''}`}>
                    <td className="py-1 pr-3 whitespace-nowrap text-brix-muted">{r.txn_date}</td>
                    <td className="py-1 pr-3 font-medium text-brix-ink">{KIND_LABEL[r.kind]}</td>
                    <td className="py-1 pr-3">{r.label}</td>
                    <td className={`py-1 pr-3 text-right font-mono ${signed == null ? 'text-brix-muted' : signed < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {signed == null ? '—' : `${signed > 0 ? '+' : ''}${signed}`}
                    </td>
                    <td className={`py-1 pr-3 text-right font-mono font-semibold ${r.bal.balance < 0 ? 'text-red-700' : 'text-brix-ink'}`}>
                      {r.bal.balance}{!r.bal.anchored ? <span className="ml-0.5 font-normal text-brix-muted">*</span> : null}
                      {r.kind === 'rental' && r.prev_balance != null && r.prev_balance !== r.quantity ? <span className="ml-1 text-[10px] font-normal text-brix-muted">(was {r.prev_balance})</span> : null}
                    </td>
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {auditId ? (
                        <Link to={`/admin/audits/${auditId}`} className="font-mono text-brix-primary hover:underline">{r.doc_number}</Link>
                      ) : r.qbo_invoice_id ? (
                        <a href={`https://app.qbo.intuit.com/app/invoice?txnId=${r.qbo_invoice_id}`} target="_blank" rel="noreferrer" className="font-mono text-brix-muted hover:underline">{r.doc_number}</a>
                      ) : (
                        <span className="font-mono text-brix-muted">{r.doc_number ?? '—'}</span>
                      )}
                      {r.kind === 'rental' && r.unit_price != null ? <span className="ml-2 text-brix-muted">{fmtMoney(r.unit_price)} × {r.quantity} = {fmtMoney(r.amount ?? 0)}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-brix-muted">Balance is what the account held at the end of that day, per cylinder type.{unanchored ? ' * No rental invoice has set a starting count yet, so these rows count from zero — a negative balance means more were picked up than we have on record as delivered.' : ''}</p>
        </div>
      )}
    </section>
  );
}
